<script setup lang="ts">
import { Button } from 'pejer/src/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'pejer/src/components/ui/form'
import { toast } from 'pejer/src/components/ui/toast'

import { Input } from 'pejer/src/components/ui/input'

import { usePejer } from '@/Pejer'
import { toTypedSchema } from '@vee-validate/zod'
import { Loader2 } from 'lucide-vue-next'
import DataStateError from 'pejer/src/components/DataState/DataStateError.vue'
import SectionCard from 'pejer/src/components/ui/section-card/SectionCard.vue'
import { ErrorObject } from 'pejer/src/types/types'
import { useForm } from 'vee-validate'
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as z from 'zod'

const store = usePejer()

const formSchema = toTypedSchema(
  z.object({
    email: z.string().email().min(2).max(50),
    password: z.string().min(2).max(50),
  })
)

const { handleSubmit } = useForm({
  validationSchema: formSchema,
})

const isLoading = ref(false)
const error = ref<ErrorObject>()
const route = useRoute()
const router = useRouter()

onMounted(() => {
  if (route.query.errorCode) {
    toast({
      title: 'You must be logged in to view this page',
      duration: 4000,
      variant: 'destructive',
    })
  }
})

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true
  const res = await store.signInUser(values.email, values.password)
  isLoading.value = false

  if ('error' in res) {
    toast({
      title: 'Error signing in',
      description: res.error,
      duration: 4000,
      variant: 'destructive',
    })
    error.value = res
    return
  }

  if (!res.data.user.isTwoFactorEnabled) {
    router.push('/login/otp/setup')
    return
  }

  router.push('/login/otp')
})
</script>

<template>
  <div class="flex flex-col gap-2 w-full justify-center items-center">
    <SectionCard class="p-8 w-full max-w-[500px]">
      <form class="w-full space-y-6 my-6" @submit="onSubmit">
        <h1 class="text-2xl font-medium">Bucky Portal</h1>
        <div class="grid gap-2">
          <FormField v-slot="{ componentField }" name="email">
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="text" placeholder="example@example.com" v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div class="grid gap-2">
          <FormField v-slot="{ componentField }" name="password">
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input type="password" v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <DataStateError v-if="error" :error="error" isAction> </DataStateError>
        <Button :disabled="isLoading" class="w-full">
          <Loader2 v-if="isLoading" class="animate-spin mr-2 w-4 h-4" /> Sign in
        </Button>
      </form>
    </SectionCard>
  </div>
</template>
